import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { transformsStore } from '../../../../../mobx/transforms.store';
import * as _ from 'lodash';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import ConfirmPopup from '../../../../components/ConfirmPopup/ConfirmPopup';
import TextFieldWrapper from '../../../../components/TextFieldWrapper/TextFieldWrapper';
import Slider from '../../../../components/Slider/Slider';
import { ITransform, TRANSFROM_MAX_TOKENS } from '../../../../../models/ITransform';
import { RectButton } from '../../../../components/buttons/RectButton/RectButton';
import { confirmAlert } from '../../../../../services/mini/alanConfirmAlert';
import { isMaxTokensValid } from '../getTransformText';

const RevertToDefaultSvgIcon = (props) => {
    return <span {...props}>
        <svg width="800px" height="800px" viewBox="0 0 16 16" version="1.1" xmlns="http://www.w3.org/2000/svg" >
            <rect width="16" height="16" id="icon-bound" fill="none" />
            <path d="M0,0l2,0l0,2.709c1.466,-1.662 3.609,-2.709 6,-2.709c4.419,0 8,3.581 8,8c0,4.419 -3.581,8 -8,8c-4.419,0 -8,-3.581 -8,-8l2,0c0,1.603 0.624,3.11 1.756,4.244c1.132,1.134 2.641,1.756 4.244,1.756c1.603,0 3.109,-0.625 4.244,-1.756c1.131,-1.135 1.756,-2.641 1.756,-4.244c0,-1.603 -0.625,-3.109 -1.756,-4.244c-1.135,-1.131 -2.641,-1.756 -4.244,-1.756c-1.603,0 -3.109,0.625 -4.244,1.756c-0.078,0.078 -0.156,0.16 -0.228,0.244l2.472,0l0,2l-6,0l0,-6Zm10.784,4.916l1.485,1.337l-5.169,5.747l-3.244,-3.206l1.406,-1.422l1.754,1.731l3.768,-4.187Z" />
        </svg>
    </span>;
}

const MAX_TOKENS_ERR_MSG = `Should be in a range from 256 to ${TRANSFROM_MAX_TOKENS}`;

const iqValues = [
    { id: 'default', label: 'Default' },
    { id: 'low', label: 'Low' },
    { id: 'normal', label: 'Normal' },
    { id: 'high', label: 'High' },
];

const testMaxTokensNumberInput = (val) => {
    const re = /^[0-9\.\b]+$/;
    if (val === '' || re.test(val)) {
        return true;
    }

    return false;
}

type TransformConfigProps = {
    transform: ITransform,
    savedTransform: ITransform,
    touched: boolean,
    disabled: boolean,
}

function TransformConfig({ transform, savedTransform, touched, disabled }: TransformConfigProps) {

    const [temp, setTemp] = useState(transform?.config?.temperature);
    const [maxTokens, setMaxTokens] = useState(transform?.config?.maxTokens);
    const [maxTokensErr, setMaxTokensErr] = useState(isMaxTokensValid(transform?.config?.maxTokens) ? null : MAX_TOKENS_ERR_MSG);
    const [isTempEditorShown, setIsTempEditorShown] = useState(false);
    const [isMaxTokensEditorShown, setIsMaxTokensEditorShown] = useState(false);

    useEffect(() => {
        if (touched === false) {
            setIsTempEditorShown(false);
            setIsMaxTokensEditorShown(false);
        }
    }, [touched]);

    useEffect(() => {
        setTemp(transform?.config?.temperature)
        setMaxTokens(transform?.config?.maxTokens)
    }, [transform?.config?.temperature, transform?.config?.maxTokens]);

    const updateTransformIqCb = useCallback((iqItem) => {
        transformsStore.updateIq(iqItem.id === 'default' ? null : iqItem.id);
    }, []);

    const onTemperatureAfterChangeCb = useCallback((val) => {
        transformsStore.updateTemperature(val);
    }, []);

    const onTemperatureChangeCb = useCallback((val) => {
        setTemp(val);
    }, []);

    const onIqResetCb = useCallback((val) => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <ConfirmPopup
                        onClose={onClose}
                        okBtnLabel="Reset"
                        okBtnCb={() => {
                            transformsStore.updateIq(null);
                            onClose();
                        }}
                        title="Reset to default value"
                        message={`Are you sure you want to reset IQ value to default?`} />
                );
            }
        });
    }, []);

    const onTemperatureResetCb = useCallback((val) => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <ConfirmPopup
                        onClose={onClose}
                        okBtnLabel="Reset"
                        okBtnCb={() => {
                            setTemp(null);
                            setIsTempEditorShown(false);
                            transformsStore.updateTemperature(null);
                            onClose();
                        }}
                        title="Reset to default value"
                        message={`Are you sure you want to reset temperature value to default?`} />
                );
            }
        });
    }, []);

    const onMaxTokensResetCb = useCallback((val) => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <ConfirmPopup
                        onClose={onClose}
                        okBtnLabel="Reset"
                        okBtnCb={() => {
                            setMaxTokens(null);
                            setIsMaxTokensEditorShown(false);
                            transformsStore.updateMaxTokens(null);
                            onClose();
                        }}
                        title="Reset to default value"
                        message={`Are you sure you want to reset max. tokens value to default?`} />
                );
            }
        });
    }, []);


    const onMaxTokensBlur = useCallback((name, value) => {
        let newVal = value;

        if (_.isString(newVal) && newVal?.trim() === '') {
            transformsStore.updateMaxTokens(null);
            setIsMaxTokensEditorShown(false);
            setMaxTokens(null);
        }

        if (_.isNumber(+newVal)) {
            setMaxTokens(+newVal);
            transformsStore.updateMaxTokens(+newVal);
        }
    }, []);

    const onMaxTokensChange = useCallback((e) => {
        let newVal = e.target.value;

        if (testMaxTokensNumberInput(newVal)) {
            if (isMaxTokensValid(+newVal)) {
                setMaxTokensErr(null);
                setMaxTokens(newVal);
                transformsStore.updateMaxTokens(newVal);
            } else {
                setMaxTokens(newVal);
                setMaxTokensErr(MAX_TOKENS_ERR_MSG);
            }
        }
    }, []);

    const discardConfigCb = useCallback((e) => {
        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <ConfirmPopup
                        onClose={onClose}
                        okBtnLabel="Discard"
                        okBtnCb={() => {
                            setIsMaxTokensEditorShown(false);
                            setIsTempEditorShown(false);
                            transformsStore.discardConfigChanges();
                            onClose();
                        }}
                        title="Discard Config Changes"
                        message={`Are you sure you want to discard your current changes in transfom config?`} />
                );
            }
        });
    }, []);

    const hasChangesInIq = transform?.config?.iq !== savedTransform?.config?.iq;
    const hasChangesInTemperature = transform?.config?.temperature !== savedTransform?.config?.temperature;

    let hasChangesInMaxTokens = false;

    try {
        const currMaxTokens = transform?.config?.maxTokens;
        const savedMaxTokens = savedTransform?.config?.maxTokens;

        if (_.isNumber(+currMaxTokens) && _.isNumber(+savedMaxTokens)) {
            hasChangesInMaxTokens = +currMaxTokens !== +savedMaxTokens;
        } else {
            hasChangesInMaxTokens = currMaxTokens !== savedMaxTokens;
        }
    } catch (error) {

    }

    const hasChanges = hasChangesInIq || hasChangesInTemperature || hasChangesInMaxTokens;

    const selectedIq = transform?.config?.iq || 'default';

    return <div className={`transform-config ${disabled ? 'transform-config__disabled' : ''}`}>

        <div className="transform-config__body">

            <div className={`transform-config__group ${hasChangesInIq ? 'has-changes' : ''}`}>
                <label>IQ:</label>
                <div className="transform-config__control-wrapper">
                    <ComboBox
                        disabled={disabled}
                        values={iqValues}
                        valueKey="id"
                        valueName="label"
                        width="100px"
                        selectedValueId={selectedIq}
                        renderItem={(item) => {
                            return <div style={{ paddingLeft: '8px' }}>{item.label}</div>;
                        }}
                        onValueSelect={updateTransformIqCb}
                    />
                    {selectedIq !== 'default' && <RevertToDefaultSvgIcon
                        title="Reset IQ to default"
                        onClick={onIqResetCb} />}
                </div>
            </div>

            <div className={`transform-config__group ${hasChangesInMaxTokens ? 'has-changes' : ''}`}>
                <label>Max. tokens:</label>
                {(maxTokens === null && !isMaxTokensEditorShown) ?
                    <span
                        className="transform-config__def-val"
                        onClick={() => {
                            transformsStore.touchConfig();
                            setIsMaxTokensEditorShown(true);
                            onMaxTokensChange({ target: { value: 6000 } });
                        }}>
                        Default
                        <i className="icon icon-alan-edit" />
                    </span> :
                    <div className="transform-config__control-wrapper">
                        <TextFieldWrapper
                            inputClassName="transform-config__max-tokens"
                            placeholder="e.g. 6000"
                            id="maxTokens"
                            name="maxTokens"
                            error={maxTokensErr}
                            value={maxTokens === null ? '' : maxTokens}
                            type="text"
                            autoComplete="false"
                            skipOnChangeAfterOnBlur={true}
                            shouldBlurBeFired={() => true}
                            onChange={onMaxTokensChange}
                            onBlur={onMaxTokensBlur} />
                        <RevertToDefaultSvgIcon
                            title="Reset max tokens to default"
                            onClick={onMaxTokensResetCb} />
                    </div>}
            </div>

            <div className={`transform-config__group ${hasChangesInTemperature ? 'has-changes' : ''}`}>
                <label>Temperature:</label>
                {(temp === null && !isTempEditorShown) ? <span
                    className="transform-config__def-val"
                    onClick={() => {
                        transformsStore.touchConfig();
                        setIsTempEditorShown(true);
                        setTemp(0.2);
                        onTemperatureAfterChangeCb(0.2);
                    }}>
                    Default
                    <i className="icon icon-alan-edit" />
                </span> :
                    <div className="transform-config__control-wrapper">
                        <Slider
                            className="transform-config__temperature"
                            min={0}
                            max={2}
                            value={temp === null ? .2 : temp}
                            step={0.1}
                            width={100}
                            fillTrack={true}
                            onAfterChange={onTemperatureAfterChangeCb}
                            onChange={onTemperatureChangeCb}
                        />
                        <RevertToDefaultSvgIcon
                            title="Reset temperature to default"
                            onClick={onTemperatureResetCb} />
                    </div>
                }
            </div>
            <div className={`transform-config__group ${hasChanges ? '' : 'disabled'}`}>
                <RectButton
                    title="Discard changes for transfom config"
                    onClick={discardConfigCb}
                    children={<i className="icon icon-alan-arrow-round-right-left-fat" />}
                />
            </div>


        </div>
    </div>;
}

export default observer(TransformConfig);
